import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {Fragment, useMemo, useState} from 'react'

import {highlightCodeToNodes} from '../shared/hast-helpers'
import hljsStyles from '../shared/hljs-tokens.module.css'
import {useLowlight} from '../shared/use-lowlight'
import {extractCodeContent, isCodeFenceSegment, splitCodeFences} from './code-fence-utils'
import styles from './HighlightedUserText.module.css'

export interface HighlightedUserTextProps {
  /** The raw user message text, which may contain fenced code blocks. */
  text: string
  /** Optional class applied to the wrapping container. */
  className?: string
  collapsible?: boolean
}

/**
 * Presentational renderer for sent user messages: prose segments render as plain
 * text and fenced code segments render as syntax-highlighted `<pre>` blocks. The
 * highlighter is lazily loaded and only when the text actually contains a fence,
 * so messages without code pay no cost.
 */
export function HighlightedUserText({text, className, collapsible = true}: HighlightedUserTextProps) {
  const lowlight = useLowlight(text.includes('```'))

  // Memoize the split + highlight: on the AHP timeline the active turn re-renders
  // on every streamed token, and without this a pasted code block (up to
  // MAX_PRETTIFY_PASTE_SIZE) would re-highlight synchronously on each one.
  const renderedSegments = useMemo(() => {
    const segments = splitCodeFences(text)
    const fenceCount = segments.filter(isCodeFenceSegment).length
    let codeBlockIndex = 0
    const nodes: React.ReactNode[] = []

    for (const [i, segment] of segments.entries()) {
      if (!isCodeFenceSegment(segment)) {
        nodes.push(<Fragment key={i}>{segment}</Fragment>)
        continue
      }

      const code = extractCodeContent(segment)
      const highlightedNodes = lowlight ? highlightCodeToNodes(lowlight, code) : null
      codeBlockIndex += 1

      if (!collapsible) {
        nodes.push(
          <pre
            key={i}
            className={clsx(styles.codeBlock, styles.standaloneCodeBlock, hljsStyles.tokens)}
            data-testid="highlighted-code-block"
          >
            <code>{highlightedNodes ?? code}</code>
          </pre>,
        )
        continue
      }

      const label = fenceCount > 1 ? `Pasted content ${codeBlockIndex}` : 'Pasted content'
      nodes.push(<SentCodeBlock key={i} code={code} highlightedNodes={highlightedNodes} label={label} />)
    }
    return nodes
  }, [text, lowlight, collapsible])

  return <div className={clsx(styles.container, className)}>{renderedSegments}</div>
}

interface SentCodeBlockProps {
  code: string
  highlightedNodes: ReturnType<typeof highlightCodeToNodes>
  label: string
}

function SentCodeBlock({code, highlightedNodes, label}: SentCodeBlockProps) {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className={clsx(styles.codeCard, collapsed && styles.collapsed)}>
      <div className={styles.header}>
        <span className={styles.languageLabel}>{label}</span>
        <IconButton
          size="small"
          icon={collapsed ? ChevronRightIcon : ChevronDownIcon}
          aria-label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed(value => !value)}
          variant="invisible"
          tooltipDirection="n"
          className={styles.headerButton}
        />
      </div>
      {!collapsed && (
        <pre className={clsx(styles.codeBlock, hljsStyles.tokens)} data-testid="highlighted-code-block">
          <code>{highlightedNodes ?? code}</code>
        </pre>
      )}
    </div>
  )
}
